import { computed, inject, Injectable, signal } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ToastrService } from 'ngx-toastr';
import { ZegoDeviceInfo } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web';

import { ModalPermissionComponent } from '../pages/meeting-management/components/modal-permission/modal-permission.component';
import { delay, isMacOS, isSafari } from '../utils/utils';
import { ZegoEngineService } from './zego-engine.service';
import { ZegoStreamService } from './zego-stream.service';

/**
 * Safari nhả camera/micro không tức thì sau khi stop track. Mở lại thiết bị ngay lập tức thì
 * track mới ở trạng thái `live` nhưng không có khung hình.
 */
const SAFARI_DEVICE_RELEASE_MS = 500;
const GET_USER_MEDIA_TIMEOUT_MS = 5000;
const DEVICE_BUSY_RETRY_MS = 1000;

@Injectable({
  providedIn: 'root',
})
export class LocalMediaPermissionService {
  nzModal = inject(NzModalService);
  private toastr = inject(ToastrService);
  private zegoEngineService = inject(ZegoEngineService);
  private zegoStreamService = inject(ZegoStreamService);

  DEFAULT_MIC_DEVICE_ID = 'default';
  DEFAULT_CAM_DEVICE_ID = 'default';

  private permissionModal!: ModalPermissionComponent;

  // private readonly _micPermission = signal<boolean>(false);
  // private readonly _camPermission = signal<boolean>(false);
  private readonly _micPermissionState = signal<'granted' | 'denied' | 'prompt'>('prompt');
  private readonly _camPermissionState = signal<'granted' | 'denied' | 'prompt'>('prompt');

  public hasCamDevice = computed(() => this.cams().length);
  public hasMicDevice = computed(() => this.mics().length);

  // readonly micPermission = this._micPermission.asReadonly();
  // readonly camPermission = this._camPermission.asReadonly();
  readonly camPermissionState = this._camPermissionState.asReadonly();
  readonly micPermissionState = this._micPermissionState.asReadonly();

  isPermissionCam = computed(() => this.camPermissionState() === 'granted');
  isPermissionMic = computed(() => this.micPermissionState() === 'granted');

  mics = signal<ZegoDeviceInfo[]>([]);
  cams = signal<ZegoDeviceInfo[]>([]);
  speakers = signal<ZegoDeviceInfo[]>([]);

  // readonly isMicPermissionGranted = computed(() => {
  //   return this.micPermission() && this.micPermissionState() !== 'denied';
  // });

  // readonly isCameraPermissionGranted = computed(() => {
  //   return this.camPermission() && this.camPermissionState() !== 'denied';
  // });

  constructor() {
    this.loadDevices();

    navigator.permissions
      .query({ name: 'camera' as PermissionName })
      .then((status) => {
        this._camPermissionState.set(status.state);
        status.onchange = () => {
          this._camPermissionState.set(status.state);
          if (status.state === 'granted') this.closeOnGranted('cam');
        };
      })
      .catch((err) => {
        console.warn('Trình duyệt không hỗ trợ API truy vấn quyền Camera', err);
      });

    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        console.log('permission mic', status.state);
        this._micPermissionState.set(status.state);
        status.onchange = () => {
          console.log('permission mic', status.state);

          // const updatedMicPermission = this.micPermission();
          this._micPermissionState.set(status.state);
          if (status.state === 'granted') this.closeOnGranted('mic');
        };
      })
      .catch((err) => {
        console.warn('Trình duyệt không hỗ trợ API truy vấn quyền Microphone', err);
      });

    this.listenZegoDeviceChange();
  }

  getPermissionState = (type: 'mic' | 'cam') =>
    type === 'mic' ? this.micPermissionState() : this.camPermissionState();

  requestCameraMicPermission = async (
    type: 'mic' | 'cam',
    hasRetried = false,
  ): Promise<boolean> => {
    try {
      // * không có thiết bị
      if ((type === 'mic' && !this.hasMicDevice()) || (type === 'cam' && !this.hasCamDevice())) {
        this.openModal({
          type,
          state: 'none',
          into: 'meeting',
        });
        return false;
      }

      // * mở popup xin quyền 1 lần
      if (this.getPermissionState(type) === 'denied') {
        const granted = await this.openModal({
          type,
          into: 'meeting',
          state: 'denied',
        });
        if (!granted) return false;
      }

      // Trên macOS vẫn gọi getUserMedia để kiểm tra lại quyền thật, vì `permissions.query`
      // cho cam/mic không đáng tin. Riêng Safari thì không: mở rồi đóng ngay một stream chỉ để
      // thăm dò, ngay trước khi caller capture thật, khiến camera thật mở lên đen hình. Đã có
      // quyền rồi thì tin vào trạng thái đã lưu.
      const isGranted = this.getPermissionState(type) === 'granted';
      if (isGranted && (!isMacOS() || isSafari())) return true;

      const stream = await this.getUserMediaWithTimeout({
        video: type === 'cam',
        audio: type === 'mic',
      });
      stream.getTracks().forEach((track) => track.stop());

      // Caller sẽ mở lại đúng thiết bị vừa đóng, phải chờ Safari nhả xong.
      if (isSafari()) await delay(SAFARI_DEVICE_RELEASE_MS);
      if (type === 'mic') {
        this._micPermissionState.set('granted');
      } else {
        this._camPermissionState.set('granted');
      }
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      switch (error.name) {
        case 'NotAllowedError': {
          if (isMacOS() && !isSafari()) {
            this.openModal({
              type,
              into: 'meeting',
              state: 'browser-blocked',
            });
            return false;
          }
          if (isMacOS()) {
            this.openModal({
              type,
              into: 'meeting',
              state: 'safari-blocked',
            });
            break;
          }
          const granted = await this.openModal({
            type,
            into: 'meeting',
            state: 'denied',
          });
          if (granted && !hasRetried) return this.requestCameraMicPermission(type, true);
          break;
        }

        case 'NotFoundError':
          this.openModal({
            type,
            state: 'none',
            into: 'meeting',
          });
          break;

        case 'NotReadableError':
        case 'AbortError':
          if (!hasRetried) {
            await delay(DEVICE_BUSY_RETRY_MS);
            return this.requestCameraMicPermission(type, true);
          }
          this.notifyDeviceBusy(type);
          break;

        case 'OverconstrainedError':
          console.log('Constraints not satisfied');
          break;

        default:
          console.log('Unknown error', error);
      }
    }
    return false;
  };

  notifyDeviceBusy(type: 'mic' | 'cam') {
    this.toastr.warning(
      `Chưa mở được ${type === 'mic' ? 'micro' : 'camera'}, thiết bị có thể đang bận hoặc đang kết nối lại. Vui lòng thử lại.`,
    );
  }

  private getUserMediaWithTimeout(constraints: MediaStreamConstraints): Promise<MediaStream> {
    const request = navigator.mediaDevices.getUserMedia(constraints);
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        request
          .then((stream) => stream.getTracks().forEach((track) => track.stop()))
          .catch(() => {});
        reject(new DOMException('getUserMedia timeout', 'AbortError'));
      }, GET_USER_MEDIA_TIMEOUT_MS);

      request.then(
        (stream) => {
          clearTimeout(timer);
          resolve(stream);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        },
      );
    });
  }

  private async recoverEndedMic() {
    if (!this.zegoStreamService.isLocalMicTrackEnded()) return;
    try {
      await this.zegoStreamService.useMicDevice(this.DEFAULT_MIC_DEVICE_ID);
    } catch (error) {
      console.error('Recover ended microphone error:', error);
    }
  }

  listenZegoDeviceChange() {
    this.zegoEngineService.engine.on(
      'audioDeviceStateChanged',
      async (updateType, deviceType, deviceInfo) => {
        if (updateType === 'ADD' && deviceType === 'Input') {
          if (!this.mics().some((m) => m.deviceID === deviceInfo.deviceID)) {
            this.mics.update((list) => [...list, deviceInfo]);
          }
          await this.recoverEndedMic();
        } else if (updateType === 'DELETE' && deviceType === 'Input') {
          const removedDeviceID = deviceInfo.deviceID;
          const removedDeviceName = deviceInfo.deviceName;
          const localStream = this.zegoStreamService.localStream()?.stream;
          this.mics.update((list) => list.filter((m) => m.deviceID !== removedDeviceID));
          if (localStream) {
            const currentMicDeviceID = this.zegoStreamService.currentMicDeviceID();
            const isCurrentInUse =
              currentMicDeviceID === removedDeviceID ||
              currentMicDeviceID === removedDeviceName ||
              (currentMicDeviceID && currentMicDeviceID.includes(removedDeviceName));
            if (!isCurrentInUse && !this.zegoStreamService.isLocalMicTrackEnded()) return;
            try {
              await this.zegoStreamService.useMicDevice(this.DEFAULT_MIC_DEVICE_ID);
            } catch (e) {
              console.error('Switch to default microphone error:', e);
              if (this.mics().length > 0) {
                this.zegoStreamService
                  .useMicDevice(this.mics()[0].deviceID)
                  .catch((error) => console.error('Switch microphone fallback error:', error));
              }
            }
          }
        }
      },
    );
    this.zegoEngineService.engine.on('videoDeviceStateChanged', (updateType, deviceInfo) => {
      if (updateType === 'ADD') {
        if (!this.cams().some((c) => c.deviceID === deviceInfo.deviceID)) {
          this.cams.update((list) => [...list, deviceInfo]);
        }
      } else {
        // Cố tình không tự chuyển sang camera khác như nhánh mic: bật một camera khác mà
        // user không chọn là vấn đề riêng tư, nên chỉ cập nhật danh sách thiết bị.
        this.cams.update((list) => list.filter((c) => c.deviceID !== deviceInfo.deviceID));
      }
    });
  }

  handleNotAllow(type: 'mic' | 'cam' | 'share-screen') {
    if (isMacOS() && !isSafari()) {
      this.openModal({
        type,
        into: 'meeting',
        state: 'browser-blocked',
      });
      return;
    }
    if (isMacOS()) {
      this.openModal({
        type,
        into: 'meeting',
        state: 'safari-blocked',
      });
      return;
    }
    // this.openModal({
    //   type,
    //   into: 'meeting',
    //   state: 'denied',
    // });
  }

  handleCheckDevicePermissionCall = async (callType: 'audio' | 'video'): Promise<boolean> => {
    const micResult = await this.handleCheckDevicePermission('mic');
    if (!micResult) return false;

    // ===== HANDLE CAMERA (OPTIONAL) =====
    if (callType === 'video') {
      if (this.hasCamDevice()) {
        // có cam thì check permission
        if (this.camPermissionState() !== 'granted') {
          try {
            await this.requestCameraMicPermission('cam');
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (e) {
            console.warn('Request camera fail → fallback audio');
          }
        }
      } else {
        console.warn('Không có camera → fallback audio');
      }
    }

    // LUÔN TRUE nếu qua được MIC
    return true;
  };

  handleCheckDevicePermission = async (type: 'mic' | 'cam'): Promise<boolean> => {
    // ===== CHECK DEVICE =====
    if (type === 'mic' && !this.hasMicDevice()) {
      this.createModalPermission(type);
      return false;
    }

    if (type === 'cam' && !this.hasCamDevice()) {
      this.createModalPermission(type);
      return false;
    }

    // ===== CHECK PERMISSION =====
    if (this.getPermissionState(type) === 'denied') {
      const granted = await this.createModalPermission(type);
      if (!granted) return false;
    }

    // ===== REQUEST PERMISSION =====
    if (this.getPermissionState(type) !== 'granted') {
      await this.requestCameraMicPermission(type);
    }

    return true;
  };

  createModalPermission(type: 'mic' | 'cam'): Promise<boolean> {
    return this.openModal({
      type,
      into: 'call',
      state: 'denied',
    });
  }

  async loadDevices() {
    try {
      const list = await this.zegoEngineService.engine.enumDevices();

      const cams = list.cameras;
      const mics = list.microphones;
      const speakers = list.speakers;

      this.mics.set(mics);
      this.cams.set(cams);
      this.speakers.set(speakers);

      return { cams, mics, speakers };
    } catch (err) {
      console.warn('loadDevices error:', err);
      return { cams: [], mics: [], speakers: [] };
    }
  }

  registerModal(permissionModal: ModalPermissionComponent) {
    this.permissionModal = permissionModal;
  }

  openModal(data: {
    type: 'mic' | 'cam' | 'share-screen';
    into: 'chat' | 'meeting' | 'call';
    state: 'granted' | 'denied' | 'prompt' | 'none' | 'browser-blocked' | 'safari-blocked';
  }): Promise<boolean> {
    if (!this.permissionModal) return Promise.resolve(false);
    return this.permissionModal.open(data);
  }

  close(granted = false) {
    this.permissionModal?.close(granted);
  }

  private closeOnGranted(type: 'mic' | 'cam') {
    this.close(this.permissionModal?.data?.type === type);
  }
}
